<template>
  <div>
    <b-row>
      <b-col cols="12" class="text-center mb-1 d-flex justify-content-end">
        <b-button variant="primary" to="/rawmaterials/add">
          <span class="text-nowrap">Add Raw Material</span>
        </b-button>
      </b-col>
    </b-row>

    <b-row>
    <b-col cols="12" md="6" class="my-1">
      <b-form-group label="Select Columns:" class="mb-0">
        <b-form-checkbox-group 
          v-model="selectedColumns" 
          :options="columnOptions"
        ></b-form-checkbox-group>
      </b-form-group>
    </b-col>
  </b-row>

    <b-row>
      <b-col cols="12" md="2" sm="2" class="my-1">
        <b-form-group class="mb-0">
          <label class="d-inline-block text-sm-left mr-50">Per page</label>
          <b-form-select id="perPageSelect" v-model="perPage" size="sm" :options="pageOptions" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-col cols="12" class="d-flex justify-content-end mb-2" style="position: relative; z-index: 1;">
      <b-button variant="outline-danger" @click="clearColumnFilters">
        Clear Filters
      </b-button>
    </b-col>

    <b-row>
      <b-col cols="12">
        <b-table striped hover responsive :per-page="perPage" :current-page="currentPage" :items="filteredData"
          :fields="dynamicFields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :sort-direction="sortDirection"
          @filtered="onFiltered">
          <!-- Filter inputs for columns -->
          <template #head(name)>
            <div class="d-flex flex-column">
              <span>Name</span>
              <b-form-input v-model="filters.name" placeholder="Filter by Name" size="sm" @input="filterTable" />
            </div>
          </template>

          <template #head(description)>
            <div class="d-flex flex-column">
              <span>Description</span>
              <b-form-input v-model="filters.description" placeholder="Filter by Description" size="sm"
                @input="filterTable" />
            </div>
          </template>

          <template #head(supplierName)>
          <div class="d-flex flex-column">
            <span>Supplier</span>
            <b-form-input v-model="filters.supplierName" placeholder="Filter by Supplier" size="sm" @input="filterTable" />
          </div>
        </template>

          <template #head(category.name)>
            <div class="d-flex flex-column">
              <span>Category</span>
              <b-form-input v-model="filters.category" placeholder="Filter by Category" size="sm"
                @input="filterTable" />
            </div>
          </template>

          <template #head(defaultUomID)>
            <div class="d-flex flex-column">
              <span>Unit of Measure</span>
              <b-form-input v-model="filters.unitOfMeasure" placeholder="Filter by Unit of Measure" size="sm"
                @input="filterTable" />
            </div>
          </template>

          <template #cell(balance)="data">
            <b-button :to="getUpdate(data.item.id)" v-b-tooltip.hover.focus.v-secondary title="View details of Stock">
              <div class="d-flex flex-shrink">
                <feather-icon icon="BoxIcon" class="mr-50" />
                <span>{{ data.item.physicalinventory }}</span>
              </div>
            </b-button>
          </template>

          <template #cell(defaultUomID)="data">
            {{ getUomName(data.item.defaultUomID) }}
          </template>

          <template #cell(purchasePrice)="data">
            Rs {{ data.item.purchasePrice.toFixed(2) }}
          </template>

          <template #cell(actions)="data">
            <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
              <template v-slot:button-content>
                <feather-icon icon="MoreVerticalIcon" size="16" class="text-body align-middle mr-25" />
              </template>
              <b-dropdown-item :to="getRawMaterialView(data.item.id)">
                <feather-icon icon="EyeIcon" class="mr-50" />
                <span>View</span>
              </b-dropdown-item>
              <b-dropdown-item :to="getRawMaterialEdit(data.item.id)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="removeRawMaterial(data.item.id)">
                <feather-icon icon="TrashIcon" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <div class="d-flex justify-content-center mb-1">
          <b-alert v-if="noResults" variant="warning" show>
            No raw materials found.
          </b-alert>
        </div>
        <div class="d-flex justify-content-center mb-1">
          <b-spinner variant="success" label="Spinning" block v-if="loading"
            style="width: 3rem; height: 3rem"></b-spinner>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="center" size="sm"
          class="my-0" />
        <b-form-group>
          Showing 
        {{ totalRows > 0 ? (perPage * (currentPage - 1) + 1) : 0 }} 
        to 
        {{ totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0 }} 
        of 
        {{ totalRows > 0 ? totalRows : 0 }} 
        entries
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BTable,
  BAvatar,
  BAlert,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BSpinner,
  BDropdown,
  BDropdownItem,
  VBTooltip,
  BFormCheckboxGroup,
} from "bootstrap-vue";

import { mapActions, mapGetters } from "vuex";
import { fnSortingDropdown } from "@/libs/customFn";
import { PermissionList } from '@/libs/defaultList';
import { HasPermission } from '@/@core/utils/validations/permissionCheck';

export default {
  components: {
    BTable,
    BAvatar,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BAlert,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BSpinner,
    BDropdown,
    BDropdownItem,
    fnSortingDropdown,
    BFormCheckboxGroup
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rawmaterialData: [],
      productName: [],
      selectedName: null,
      selectedCategory: null,
      RawMaterialListing: [],
      categoryName: [],
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 0,
      currentPage: 0,
      sortBy: "",
      noResults: false,
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      totalBalance: null,
      infoModal: {
        id: "info-modal",
        title: "",
        content: "",
      },
      selectedColumns: ['name', 'description', 'supplierName', 'category.name', 'defaultUomID', 'lowStock', 'purchasePrice', 'balance', 'actions',], // Default selected columns
      columnOptions: [
        
        { value: 'description', text: 'description' },
        { value: 'category.name', text: 'Category' },
        { value: 'supplierName', text: 'Supplier' },
        { value: 'defaultUomID', text: 'Unit of Measure' },
        { value: 'lowStock', text: 'Re-Order Level' },
        { value: 'purchasePrice', text: 'Unit Price' },
        { value: 'balance', text: 'View Balance' },
        
      ],
      // fields: [
      //   { key: "name", label: "name", sortable: true },
      //   { key: "description", label: "description", sortable: true },
      //   { key: "supplierName", label: "Supplier", sortable: true },
      //   { key: "category.name", label: "Category", sortable: true },
      //   { key: "defaultUomID", label: "Unit of Measure", sortable: true },
      //   { key: "lowStock", label: "Re-Order Level", sortable: true },
      //   { key: "purchasePrice", label: "Unit Price", sortable: true },
      //   { key: "balance", label: "View Balance" },
      //   { key: "actions" },
      // ],
      filters: {
        name: "",
        description: "",
        supplier: "",
        category: "",
        unitOfMeasure: "",
      },
    };
  },
  computed: {

    fields() {
  const baseFields = [
      { key: "name", label: "name", sortable: true },
        { key: "description", label: "description", sortable: true },
        { key: "supplierName", label: "Supplier", sortable: true },
        { key: "category.name", label: "Category", sortable: true },
        { key: "defaultUomID", label: "Unit of Measure", sortable: true },
        { key: "lowStock", label: "Re-Order Level", sortable: true },
        { key: "purchasePrice", label: "Unit Price", sortable: true },
        { key: "balance", label: "View Balance" },
    { key: "actions", label: "Actions", sortable: false },
  ];

  if (this.HasPermission(this.PermissionList.Inventory_Finance)) {
    baseFields.splice(3, 0, { key: "purchasePrice", label: "Unit Price", sortable: true }); // Insert 'total' in the right position
  }

  return baseFields;
},

    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter((f) => f.sortable)
        .map((f) => ({ text: f.label, value: f.key }));
    },
    ...mapGetters("itemBalanceModule", {
      itemBalances: "itemBalances",
      balanceLoading: "loading",
    }),
    ...mapGetters("rawmaterialModule", {
      RawMaterialList: "rawmaterials",
      loading: "loading",
    }),
    ...mapGetters("itemUomModule", {
      itemuom: "itemuom",
      loading: "loading",
    }),
    dynamicFields() {
      return this.fields.filter(field => this.selectedColumns.includes(field.key));
    },
    filteredData() {
      return this.RawMaterialListing.filter((item) => {
        // Filter by Name
        const matchesName = this.filters.name
          ? item.name.toLowerCase().includes(this.filters.name.toLowerCase())
          : true;

        // Filter by Description
        const matchesDescription = this.filters.description
          ? item.description &&
          item.description
            .toLowerCase()
            .includes(this.filters.description.toLowerCase())
          : true;

        // Filter by Supplier
        const matchesSupplier = this.filters.supplierName
          ? item.supplierName && item.supplierName.toLowerCase().includes(this.filters.supplierName.toLowerCase())
          : true;

        // Filter by Category
        const matchesCategory = this.filters.category
          ? item.category && item.category.name.toLowerCase().includes(this.filters.category.toLowerCase())
          : true;

        // Filter by Unit of Measure
        const uomName = this.getUomName(item.defaultUomID) || "";
        const matchesUnitOfMeasure = this.filters.unitOfMeasure
          ? uomName.toLowerCase().includes(this.filters.unitOfMeasure.toLowerCase())
          : true;

        // Combine all filter conditions
        return matchesName && matchesDescription&& matchesSupplier && matchesCategory && matchesUnitOfMeasure;
      });
    },
  },
  methods: {
    ...mapActions("itemUomModule", ["getItemUOMListAction"]),
    ...mapActions("itemBalanceModule", ["getItemBalanceListAction"]),
    clearFilters() {
      this.selectedName = "";
      this.selectedCategory = "";

      this.RawMaterialListing = [...this.RawMaterialList];
      this.totalRows = this.leads.length;
      this.currentPage = 1;
    },
    orderFilterChanged() {
      this.RawMaterialListing = this.RawMaterialList;
      if (this.selectedName) {
        this.RawMaterialListing = this.RawMaterialListing.filter((obj) => {
          return obj.id === this.selectedName;
        });
      }
      if (this.selectedCategory) {
        this.RawMaterialListing = this.RawMaterialListing.filter((obj) => {
          return obj.category.id === this.selectedCategory;
        });
      }

      ///code for category filter here

      this.totalRows = this.RawMaterialListing.length;
      this.currentPage = 1;
    },
    getUomName(id) {
      let uom = this.itemuom.find((uom) => uom.id == id);
      if (uom) {
        return uom.name;
      }
      return "";
    },
    getUpdate(item) {
      return "/rawmaterials/stockTransaction/" + item;
    },
    getRawMaterialView(item) {
      return "/rawmaterials/view/" + item;
    },
    getRawMaterialEdit(item) {
      return "/rawmaterials/edit/" + item;
    },
    removeRawMaterial(id) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.removeRawMaterialAction(id)
            .then(async () => {
              this.$swal({
                title: "Deleted!",
                text: "This raw material has been deleted.",
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-success",
                },
              });
              await this.reloadRawMaterialList();
            })
            .catch((error) => {
              this.$swal({
                title: "Error!",
                text: " Failed! " + error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                buttonsStyling: false,
              });
            });
        } else if (result.dismiss === "cancel") {
          this.$swal({
            title: "Cancelled",
            icon: "error",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
        }
      });
    },
    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`;
      this.infoModal.content = JSON.stringify(item, null, 2);
      this.$root.$emit("bv::show::modal", this.infoModal.id, button);
    },
    resetInfoModal() {
      this.infoModal.title = "";
      this.infoModal.content = "";
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
      this.noResults = filteredItems.length === 0;
    },
    ...mapActions("rawmaterialModule", [
      "getRawMaterialListAction",
      "removeRawMaterialAction",
    ]),
    filterTable() {
      this.currentPage = 1; // Reset pagination when a filter is applied
    },
    clearColumnFilters() {
      this.filters = {
        name: "",
        description: "",
        supplier: "",
        category: "",
        unitOfMeasure: "",
      };
      this.filter = null;
      this.currentPage = 1;
    },
    async reloadRawMaterialList(){
      await this.getRawMaterialListAction().then(() => {
        
        // Initialize totalRows and RawMaterialListing
        this.totalRows = this.RawMaterialList.length;
        this.RawMaterialListing = this.RawMaterialList;

        // Initialize dropdown options
        this.productName = [{ value: "", text: "All" }];
        this.categoryName = [{ value: "", text: "All" }];

        let addedCategoryIds = new Set(); // To track added category IDs

        // Process the raw materials
        this.RawMaterialListing = this.RawMaterialList.map((rawMaterial) => {
          // Add productName to dropdowns
          this.productName.push({ value: rawMaterial.id, text: `${rawMaterial.name}` });

          // Add categoryName to dropdowns if not already added
          if (rawMaterial.category && !addedCategoryIds.has(rawMaterial.category.id)) {
            this.categoryName.push({
              value: rawMaterial.category.id,
              text: `${rawMaterial.category.name}`,
            });
            addedCategoryIds.add(rawMaterial.category.id);
          }

          if (Array.isArray(this.itemBalances)) {
            // Calculate total balance for the raw material
            const totalBalance = this.itemBalances
              .filter(balance => balance.itemId === rawMaterial.id) // Filter for current raw material
              .reduce((sum, balance) => sum + (balance.physicalInventory ?? 0), 0); // Sum the physicalInventory

            // Assign the totalBalance to rawMaterial
            rawMaterial.physicalinventory = totalBalance;
          } else {
            console.error('itemBalances is not an array:', this.itemBalances);
          }
          return rawMaterial; // Return the modified raw material
        });

        // Sort dropdowns
        this.productName = fnSortingDropdown(this.productName, "asc", "text");
        this.categoryName = fnSortingDropdown(this.categoryName, "asc", "text");
      })
      .catch((response) => {
        console.log(response);
      });
    }
  },
  async mounted() {
    await this.getItemBalanceListAction();
    await this.reloadRawMaterialList();
    await this.getItemUOMListAction();
  },
  setup(props){
    return{
      PermissionList,
      HasPermission
    }
  }
};
</script>
